/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA6000-G2 Platform Configuration
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { PlatformConfig } from "./PlatformConfig";
import { ma6000g2HierarchyBuilder } from "../builders/MA6000G2HierarchyBuilder";

export class MA6000G2PlatformConfig implements PlatformConfig {
  platformId = "ma6000g2";
  displayName = "MA6000-G2";
  hierarchyBuilder = ma6000g2HierarchyBuilder;

  matches(platformId: string): boolean {
    const normalized = platformId?.toLowerCase().replace(/[-_\s]/g, "") || "";
    return normalized.includes("ma6000g2");
  }

  findDeviceForNode(
    nodeId: string,
    node: IInventoryGraphNode | undefined,
    listData: IRemoteEquipment[],
    rootId: string
  ): IRemoteEquipment | null {
    const isRootRoute = (route?: string) => {
      const r = (route || "").trim();
      return r === "" || r === "0" || r === "0.0.0.0";
    };

    if (nodeId === rootId || node?.type === "site") {
      const primaryDcu = listData.find((d) => {
        const route = d.topology || "";
        const equipmentType = d.equipmentType?.toUpperCase() || "";
        return isRootRoute(route) && equipmentType.includes("DCU");
      });
      if (primaryDcu) return primaryDcu;
    }

    const direct = listData.find((d) => d.objectKey === nodeId || d.managedObject === nodeId);
    if (direct) return direct;

    if (node?.label) {
      const bySerial = listData.find((d) => d.serialNumber === node.label);
      if (bySerial) return bySerial;
    }

    if (node?.ipAddress) {
      const byIp = listData.find((d) => d.ipAddress === node.ipAddress);
      if (byIp) return byIp;
    }

    return null;
  }

  getRootLedColor(listData: IRemoteEquipment[]): string {
    const isRootRoute = (route?: string) => {
      const r = (route || "").trim();
      return r === "" || r === "0" || r === "0.0.0.0";
    };

    const rootDcu = listData.find((d) => {
      const route = d.topology || "";
      const equipmentType = d.equipmentType?.toUpperCase() || "";
      return isRootRoute(route) && equipmentType.includes("DCU");
    });

    if (!rootDcu) return "#9e9e9e";

    const status = rootDcu.status?.toString().toLowerCase() || "";
    if (status === "connected" || status === "online") return "#4caf50";
    if (status === "disconnected" || status === "offline") return "#f44336";
    if (status === "warning" || status === "minor") return "#ff9800";
    if (status === "critical" || status === "major") return "#f44336";
    return "#9e9e9e";
  }

  metadata = {
    rootEquipmentType: "DCU",
  };
}
